"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Header = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const scaffold_1 = require("@groma-dev/scaffold");
const react_intl_1 = require("react-intl");
const ThemeProvider_1 = require("../../utils/ThemeProvider");
const Link_1 = require("../Link/Link");
const Utils_1 = require("../../utils/Utils");
const icons_1 = require("../../assets/icons");
const Icon_1 = require("../Icon/Icon");
const Button_1 = require("../Button/Button");
const theme_1 = require("../../themes/typings/theme");
const logos_1 = require("../../assets/logos");
const Spinner_1 = require("../Spinner/Spinner");
const StyleUtils_1 = require("../../utils/StyleUtils");
const Header_styles_1 = require("./styles/Header.styles");
const NavBar_1 = require("./NavBar");
const Menu_1 = require("./Menu");
const defaultNavItems_1 = require("./defaultNavItems");
const Account_1 = require("./Account");
const BentoBox_1 = require("./BentoBox");
const __DEV__ = process.env.NODE_ENV !== 'production';
const Header = (props) => {
    var _a, _b, _c;
    const theme = (0, ThemeProvider_1.useTheme)();
    const intl = (0, react_intl_1.useIntl)();
    const { account, className = '', height = StyleUtils_1.headerHeight, loading, loginItems = defaultNavItems_1.loginItems, logo, logoUrl, mobileBreakpoint = theme.breakpoints.$md, navItems = defaultNavItems_1.defaultNavItems, stickyHeader = true, testId, bentoBox, domains, } = props;
    if (!theme) {
        if (__DEV__) {
            console.error('`Header` component must be a child of a `ThemeProvider`');
        }
        return null;
    }
    if (!intl) {
        if (__DEV__) {
            console.error('`Header` component must be a child of a `IntlProvider`');
        }
        return null;
    }
    const [isMobile, setIsMobile] = (0, react_1.useState)((_b = ((_a = global.window) === null || _a === void 0 ? void 0 : _a.innerWidth) <= mobileBreakpoint) !== null && _b !== void 0 ? _b : false);
    const [menuOpen, setMenuOpen] = (0, react_1.useState)(false);
    const [showLoginDropdown, setShowLoginDropdown] = (0, react_1.useState)(false);
    const [showAccount, setShowAccount] = (0, react_1.useState)(false);
    const loginButtonContainer = (0, react_1.createRef)();
    const accountButtonContainer = (0, react_1.createRef)();
    if (!theme) {
        if (__DEV__) {
            console.error('`Header` component must be a child of a `ThemeProvider`');
        }
        return null;
    }
    const styles = (0, Header_styles_1.getHeaderStyles)({ height });
    const dti = (0, Utils_1.getTestIdAttribute)(testId);
    let headerLogo = logo;
    if (!headerLogo) {
        headerLogo =
            theme.__base === theme_1.GlobalBaseTheme.DARK ? ((0, jsx_runtime_1.jsx)(logos_1.MarkLightLogo, { className: styles.logo })) : ((0, jsx_runtime_1.jsx)(logos_1.MarkDarkLogo, { className: styles.logo }));
    }
    const handleOpenMenu = () => {
        document.body.style.overflow = 'hidden';
        setMenuOpen(true);
    };
    const handleCloseMenu = () => {
        document.body.style.removeProperty('overflow');
        setMenuOpen(false);
    };
    const handleShowDropdown = () => {
        setShowLoginDropdown(true);
    };
    const handleOnKeyPress = (e) => {
        if (e.key === 'enter') {
            handleShowDropdown();
        }
    };
    const handleHideDropdown = (e) => {
        if (!e.relatedTarget)
            return setShowLoginDropdown(false);
        if (loginButtonContainer.current && e.relatedTarget) {
            const isRelatedTarget = loginButtonContainer.current.contains(e.relatedTarget);
            if (showLoginDropdown && !isRelatedTarget) {
                setShowLoginDropdown(false);
            }
        }
    };
    const handleShowAccount = () => {
        setShowAccount(true);
    };
    const handleHideAccount = (e) => {
        if (!e.relatedTarget)
            return setShowAccount(false);
        if (accountButtonContainer.current && e.relatedTarget) {
            const isRelatedTarget = accountButtonContainer.current.contains(e.relatedTarget);
            if (showAccount && !isRelatedTarget) {
                setShowAccount(false);
            }
        }
    };
    const resizeHandler = () => {
        if (window.innerWidth <= mobileBreakpoint) {
            setIsMobile(true);
        }
        else
            setIsMobile(false);
    };
    (0, react_1.useEffect)(() => {
        resizeHandler();
        window.addEventListener('resize', resizeHandler);
        return () => {
            // Cleanup the event listener
            window.removeEventListener('resize', resizeHandler);
        };
    }, []);
    const userAvatar = () => {
        const { user } = account;
        const userLetters = `${user.first_name.charAt(0).toUpperCase()}${user.last_name.charAt(0).toUpperCase()}`;
        return ((0, jsx_runtime_1.jsx)(Button_1.Button, { className: (0, StyleUtils_1.classNames)(styles.accountButton), onClick: handleShowAccount, testId: "accountButton", children: userLetters.length > 0 ? userLetters : user.email.charAt(0).toUpperCase() }));
    };
    return ((0, jsx_runtime_1.jsx)("header", Object.assign({ id: "gromaHeader", className: (0, StyleUtils_1.classNames)('Header-local', styles.headerContainer, className, stickyHeader && styles.stickyHeader) }, dti, { children: (0, jsx_runtime_1.jsxs)("div", { className: (0, StyleUtils_1.classNames)('Header-mainContainer-local', styles.mainContainer, className), style: { height: `${height}px`, maxHeight: `${height}px`, minHeight: `${height}px` }, children: [(0, jsx_runtime_1.jsxs)("div", { className: (0, StyleUtils_1.classNames)('Header-mainWrapper-local', styles.mainWrapper), children: [(0, jsx_runtime_1.jsx)("div", { className: (0, StyleUtils_1.classNames)('Header-leftContent-local', styles.leftContent), children: (0, jsx_runtime_1.jsx)(Link_1.Link, { href: logoUrl !== null && logoUrl !== void 0 ? logoUrl : (0, scaffold_1.constructUrlWithParams)('/'), className: (0, StyleUtils_1.classNames)(styles.logoLink), "aria-label": "Groma Logo", testId: "headerLogoLink", children: headerLogo }) }), (0, jsx_runtime_1.jsx)("div", { className: (0, StyleUtils_1.classNames)('Header-centerContent-local', styles.centerContent), children: !isMobile && (0, jsx_runtime_1.jsx)(NavBar_1.NavBar, { navItems: navItems, domains: domains }) }), (0, jsx_runtime_1.jsxs)("div", { className: (0, StyleUtils_1.classNames)('Header-rightContent-local', styles.rightContent), children: [(bentoBox === null || bentoBox === void 0 ? void 0 : bentoBox.enabled) && (0, jsx_runtime_1.jsx)(BentoBox_1.BentoBox, Object.assign({}, bentoBox, { account: account, domains: domains })), !isMobile ? (loading ? ((0, jsx_runtime_1.jsx)(Spinner_1.Spinner, { testId: "accountLoadingSpinner", maxHeight: "40px", color: theme.colors.$content100, className: (0, StyleUtils_1.classNames)(styles.loadingSpinner) })) : account ? ((0, jsx_runtime_1.jsxs)("div", { role: "presentation", tabIndex: -1, className: (0, StyleUtils_1.classNames)(styles.accountContainer), ref: accountButtonContainer, onBlur: handleHideAccount, children: [userAvatar(), showAccount && (0, jsx_runtime_1.jsx)(Account_1.Account, Object.assign({}, account, { domains: domains }))] })) : ((0, jsx_runtime_1.jsxs)("div", { role: "button", tabIndex: 0, ref: loginButtonContainer, className: (0, StyleUtils_1.classNames)(styles.loginButtonContainer), onClick: handleShowDropdown, onKeyDown: handleOnKeyPress, onBlur: handleHideDropdown, onFocus: handleShowDropdown, onMouseEnter: handleShowDropdown, onMouseLeave: handleHideDropdown, children: [(0, jsx_runtime_1.jsx)("p", { className: styles.loginButton, children: "Sign In" }), showLoginDropdown && ((0, jsx_runtime_1.jsx)("div", { className: (0, StyleUtils_1.classNames)(styles.loginDropdown), children: (_c = loginItems === null || loginItems === void 0 ? void 0 : loginItems.subNavItems) === null || _c === void 0 ? void 0 : _c.map((sni) => ((0, jsx_runtime_1.jsx)(Link_1.Link, { href: `${sni.experience ? (0, Utils_1.getDomainBasedOnExperience)(domains, sni.experience) : ''}${sni.href}`, variant: Link_1.LinkVariants.NAV, fullWidth: true, className: (0, StyleUtils_1.classNames)(styles.portalLink), useRouter: !sni.isExternal, disabled: sni.disabled, target: sni.openNewTab ? '_blank' : '_self', onClick: () => {
                                                    setTimeout(() => setShowLoginDropdown(false), 0);
                                                }, children: sni.label }, sni.id))) }))] }))) : ((0, jsx_runtime_1.jsx)("button", Object.assign({ className: (0, StyleUtils_1.classNames)('Header-menuButton-local', styles.menuButton), onClick: menuOpen ? handleCloseMenu : handleOpenMenu }, (0, Utils_1.getTestIdAttribute)('menuButton'), { children: (0, jsx_runtime_1.jsx)(Icon_1.Icon, { component: menuOpen ? icons_1.CloseIcon : icons_1.MenuIcon, size: Icon_1.IconSize.MEDIUM }) })))] })] }), isMobile && ((0, jsx_runtime_1.jsx)(Menu_1.Menu, { isOpen: menuOpen, headerHeight: height, navItems: navItems, handleCloseMenu: handleCloseMenu, account: account, domains: domains }))] }) })));
};
exports.Header = Header;
